/**
 * Plan catalog API contracts (plan #004, U5).
 *
 * A Plan belongs to a Product and grants features via PlanFeature links
 * (each referencing a `Feature` by public id — KTD-4). Feature links are
 * managed as a full set on create/update (full-payload round-trip).
 *
 * Endpoints (all super-admin gated, platform-global):
 *  - GET  /v1/catalog/plans?productId=<id>   → list plans (optionally by product)
 *  - POST /v1/catalog/plans                   → create a plan (+ feature links)
 *  - PATCH /v1/catalog/plans/:id              → update a plan (+ replace feature links)
 */

import { z } from 'zod'
import { PlanSchema } from '../../schemas/entities/plan'

/**
 * A single plan→feature link in a create/update request. References the
 * granted `Feature` by its public id (KTD-4).
 */
export const PlanFeatureLinkInputSchema = z.object({
  featureId: z.string().min(1, 'featureId is required'),
  isEnabled: z.boolean().optional(),
  config: z.record(z.string(), z.unknown()).nullable().optional()
})
export type PlanFeatureLinkInput = z.infer<typeof PlanFeatureLinkInputSchema>

// ----- Create -----

export const CreatePlanRequestSchema = z.object({
  productId: z.string().min(1, 'productId is required'),
  slug: z
    .string()
    .trim()
    .min(1, 'Slug is required')
    .regex(/^[a-z0-9-]+$/, 'Slug must be lowercase alphanumeric with hyphens'),
  name: z.string().trim().min(1, 'Name is required'),
  description: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
  features: z.array(PlanFeatureLinkInputSchema).optional()
})
export type CreatePlanRequest = z.infer<typeof CreatePlanRequestSchema>

// ----- Update (identity fixed; features, when present, REPLACE the set) -----

export const UpdatePlanRequestSchema = z.object({
  name: z.string().trim().min(1).optional(),
  description: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
  /** When provided, fully replaces the plan's feature links (full-payload round-trip). */
  features: z.array(PlanFeatureLinkInputSchema).optional()
})
export type UpdatePlanRequest = z.infer<typeof UpdatePlanRequestSchema>

// ----- Responses -----

export const PlanResponseSchema = z.object({
  data: PlanSchema,
  message: z.string()
})
export type PlanResponse = z.infer<typeof PlanResponseSchema>

export const ListPlansResponseSchema = z.object({
  data: z.array(PlanSchema),
  message: z.string()
})
export type ListPlansResponse = z.infer<typeof ListPlansResponseSchema>
